import classNames from 'classnames';
import { ErrorMessage, Field, Form, Formik, useFormikContext } from 'formik';
import countries from 'i18n-iso-countries';
import { Yup } from '@solid/shared/services/validation.service';
import { AddressMatchSchema } from '../../../services/check.service';
import styles from './CheckIndividualTestSearchForm.module.scss';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
type Values = Yup.InferType<typeof AddressMatchSchema>;

interface CheckIndividualTestSearchFormProps {
  onSubmit?: (values: Values) => void;
  prefillValues?: Values;
}

const FormWatcher: React.FC<{ prefillValues: Values }> = ({ prefillValues }) => {
  const { setValues } = useFormikContext();

  useEffect(() => {
    if (prefillValues) {
      setValues(prefillValues);
    }
  }, [prefillValues, setValues]);

  return null;
};

const CheckIndividualTestSearchForm: React.FC<CheckIndividualTestSearchFormProps> = ({ onSubmit, prefillValues }) => {
  const isReadOnly = !!prefillValues;
  const { t } = useTranslation();

  return (
    <Formik
      initialValues={
        prefillValues ??
        ({
          name: '',
          street: '',
          zipCode: '',
          city: '',
          country: '',
        } as Values)
      }
      onSubmit={values => onSubmit(values)}
      validationSchema={AddressMatchSchema}>
      <div className={styles.formContainer}>
        <FormWatcher prefillValues={prefillValues} />

        <Form className={classNames(styles.form)}>
          <div className='form-field'>
            <label htmlFor='name'>{t('checks.individualTest.searchForm.name')}</label>
            <div className='control'>
              <Field
                type='text'
                name='name'
                id='name'
                autoComplete='organization'
                required
                readOnly={isReadOnly}
              />
            </div>
            <ErrorMessage component='p' className='help is-danger' name='name' />
          </div>

          <div className='form-field'>
            <label htmlFor='street'>{t('checks.individualTest.searchForm.street')}</label>
            <div className='control'>
              <Field type='text' name='street' id='street' readOnly={isReadOnly} />
            </div>
            <ErrorMessage component='p' className='help is-danger' name='street' />
          </div>

          <div className='form-row'>
            <div className='form-field'>
              <label htmlFor='city'>{t('checks.individualTest.searchForm.city')}</label>
              <div className='control'>
                <Field type='text' name='city' id='city' readOnly={isReadOnly} />
              </div>
              <ErrorMessage component='p' className='help is-danger' name='city' />
            </div>
            <div className='form-field'>
              <label htmlFor='zipCode'>{t('checks.individualTest.searchForm.zipCode')}</label>
              <div className='control'>
                <Field type='text' name='zipCode' id='zipCode' readOnly={isReadOnly} />
              </div>
              <ErrorMessage component='p' className='help is-danger' name='zipCode' />
            </div>
          </div>

          <div className='form-field'>
            <label htmlFor='country'>{t('checks.individualTest.searchForm.country')}</label>
            <div className='control'>
              <Field component='select' type='select' name='country' id='country' disabled={isReadOnly}>
                <option value=''>{t('common.pleaseChoose')}</option>
                {Object.entries(countries.getNames('de'))
                  .sort((a, b) => a[1].localeCompare(b[1]))
                  .map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
              </Field>
            </div>
            <ErrorMessage component='p' className='help is-danger' name='country' />
          </div>

          {isReadOnly === false && (
            <div className='form-field'>
              <button className='btn is-primary' type='submit'>
                {t('checks.individualTest.searchForm.button')}
              </button>
            </div>
          )}
        </Form>
      </div>
    </Formik>
  );
};

export default CheckIndividualTestSearchForm;
